import { getDriver } from "@theme/1scp-scroll";

// A non-modal `<dialog>`, so it stacks under the header instead of in the top layer.
// Because it is not modal, the inert page, focus and Escape are handled here.
const INERT = "main, .shopify-section-group-footer-group";

class Menu extends HTMLElement {
  #dialog = null;
  #toggle = null;

  connectedCallback() {
    if (this.parentElement !== document.body) {
      // Moved to <body> because the header blends with `difference` and would blend the sheet too.
      // The editor re-renders the header with a fresh copy, so the old one is removed rather than doubled.
      for (const old of document.querySelectorAll("body > scpd-menu")) old.remove();
      document.body.append(this);
      return;
    }
    this.#dialog = this.querySelector("dialog");
    this.#toggle = document.querySelector("[data-scpd-menu-open]");
    // Taken on the document: the theme's declarative `on:click` only reaches its own components.
    document.addEventListener("click", this.#onDocumentClick);
    // Capture, so the menu is out of the way before the cart drawer, which listens on the bag, opens.
    document.addEventListener("click", this.#onCartClick, true);
    document.addEventListener("keydown", this.#onKey);
    this.addEventListener("click", this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.#onDocumentClick);
    document.removeEventListener("click", this.#onCartClick, true);
    document.removeEventListener("keydown", this.#onKey);
  }

  open() {
    if (!this.#dialog || this.#dialog.open) return;
    const bar = document.querySelector(".header__top");
    if (bar) this.style.setProperty("--scpd-menu-bar", `${bar.getBoundingClientRect().bottom}px`);
    // The page reserves a scrollbar lane (`scrollbar-gutter: stable`), so the content is pulled in by it.
    // The root's box is measured because `clientWidth` reports the whole window when the lane is reserved.
    const lane = window.innerWidth - document.documentElement.getBoundingClientRect().width;
    this.style.setProperty("--scpd-menu-lane", `${lane}px`);

    this.#dialog.show();
    document.documentElement.classList.add("scpd-menu-open");
    for (const element of document.querySelectorAll(INERT)) element.inert = true;
    this.#setToggle(true);
    getDriver().lenis?.stop();
    document.documentElement.style.overflow = "hidden";
    this.#dialog.querySelector("a[href]")?.focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.#dialog?.open) return;
    this.#dialog.close();
    document.documentElement.classList.remove("scpd-menu-open");
    for (const element of document.querySelectorAll(INERT)) element.inert = false;
    this.#setToggle(false);
    getDriver().lenis?.start();
    document.documentElement.style.overflow = "";
    if (restoreFocus) this.#toggle?.focus({ preventScroll: true });
  }

  #setToggle(open) {
    if (!this.#toggle) return;
    const { scpdMenuLabel, scpdMenuCloseLabel } = this.#toggle.dataset;
    const label = open ? scpdMenuCloseLabel : scpdMenuLabel;
    if (label) this.#toggle.textContent = label;
    this.#toggle.setAttribute("aria-expanded", String(open));
  }

  #onDocumentClick = (event) => {
    const toggle = event.target.closest?.("[data-scpd-menu-open]");
    if (!toggle) return;
    event.preventDefault();
    if (this.#dialog?.open) this.close();
    else this.open();
  };

  #onCartClick = (event) => {
    if (!this.#dialog?.open) return;
    if (event.target.closest?.(".cart-drawer-button")) this.close({ restoreFocus: false });
  };

  #onKey = (event) => {
    if (event.key === "Escape" && this.#dialog?.open) {
      event.preventDefault();
      this.close();
    }
  };

  // A link to the current page would otherwise leave the menu standing over it.
  #onClick = (event) => {
    if (event.target === this.#dialog) {
      this.close();
      return;
    }
    const expand = event.target.closest("[data-scpd-menu-expand]");
    if (expand) {
      expand.setAttribute("aria-expanded", String(expand.getAttribute("aria-expanded") !== "true"));
      return;
    }
    const link = event.target.closest("a[href]");
    if (link && !event.metaKey && !event.ctrlKey) this.close({ restoreFocus: false });
  };
}

if (!customElements.get("scpd-menu")) customElements.define("scpd-menu", Menu);
